import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY } from 'src/constants/invoice/constants'
import { PartnerInvoicesTableFields } from 'src/constants/invoice/enums'
import {
  PARTNER_INVOICE_DEFAULT_HIDDEN_FIELDS,
  buildPartnerInvoiceColumns,
} from 'src/constants/invoice/partnerInvoiceGrid'
import type { ComposedInvoice, PartnerInvoice, PendingInvoiceAction } from 'src/interfaces/invoice'
import { getComposedInvoiceLabel } from 'src/utils/invoiceLabels'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'

interface Props {
  composedInvoice: ComposedInvoice
  canManage: boolean
  onRunAction(action: PendingInvoiceAction): void
}

const getRowId = (row: PartnerInvoice) => {
  return row.id
}

const UNHIDEABLE_COLUMNS: Array<string> = [
  PartnerInvoicesTableFields.invoice,
  PartnerInvoicesTableFields.actions,
]

/** The `{ excluded, pinned }` shape `useGridColumns` itself persists under a storage key. */
interface StoredColumnsState {
  excluded?: Array<string>
  pinned?: Array<string>
}

/**
 * Must override BOTH `GridTable`'s base `h-[400px]` AND each of its `@media (min-height: …)`
 * tiers. Emotion's stylis hoists nested at-rules after the base rule, so an appended plain
 * `h-full` loses to every tier above 830px of viewport height — the nested grid would demand
 * 520-1100px inside a ~200px detail panel. Each tier is therefore restated here.
 *
 * Do not reach for `!important`, and do not edit `GridTable` — five other tables depend on it.
 */
const nestedTableStyles = [
  tw`h-full`,
  {
    '@media (min-height: 830px)': tw`h-full`,
    '@media (min-height: 1000px)': tw`h-full`,
    '@media (min-height: 1440px)': tw`h-full`,
    '@media (min-height: 2000px)': tw`h-full`,
  },
]

const PartnerInvoicesTableComponent = ({ composedInvoice, canManage, onRunAction }: Props) => {
  const composedInvoiceLabel = useMemo(() => {
    return getComposedInvoiceLabel(composedInvoice)
  }, [composedInvoice])

  // A fresh array each render would reset the grid's column state, so the factory is memoized.
  const partnerInvoiceColumns = useMemo(() => {
    return buildPartnerInvoiceColumns(composedInvoiceLabel, onRunAction, canManage)
  }, [composedInvoiceLabel, onRunAction, canManage])

  const { includedColumns } = useGridColumns(
    partnerInvoiceColumns,
    PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY,
  )

  const [hasStoredColumnPreference, setHasStoredColumnPreference] = useState<boolean>(false)

  /**
   * `useGridColumns`'s `handleAddExcludedColumn` / `handleRemoveExcludedColumn` are not usable
   * here: both close over `excludedColumns` (deps `[excludedColumns]`) and calling several in one
   * synchronous `forEach` has every call read the same pre-update array, so only the LAST write
   * survives. This table's controlled visibility model can hand the change handler ~21 toggles
   * at once (the default hidden set), which would collapse to a single-element excluded list.
   * The hook does not expose a bulk setter (`handleSetExcludedColumns` is internal, not
   * returned), and it may not be modified — five other tables depend on its current shape. So
   * this table computes the next excluded set in one pass locally, mirrors it into this state,
   * and writes it to the SAME localStorage key/shape the hook itself would have written, rather
   * than routing it back through the hook's setters.
   */
  const [excludedColumnsOverride, setExcludedColumnsOverride] = useState<Array<string> | null>(null)

  /**
   * Hydrated in an effect, NOT a lazy initializer, so it lands in the same commit as
   * `useGridColumns`'s own localStorage read (utils/hooks/grid/useGridColumns.ts) — that hook
   * populates `excludedColumns` from an effect too. Reading this one synchronously would make
   * the first render believe a stored preference exists while the exclusion list backing it is
   * still empty, and every one of the ~30 columns would paint before snapping back to the nine
   * defaults.
   */
  useEffect(() => {
    setHasStoredColumnPreference(
      localStorage.getItem(PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY) !== null,
    )
  }, [])

  const columnVisibilityModel = useMemo(() => {
    return partnerInvoiceColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      if (excludedColumnsOverride !== null) {
        accumulator[column.field] = !excludedColumnsOverride.includes(column.field)

        return accumulator
      }

      if (hasStoredColumnPreference) {
        accumulator[column.field] = includedColumns.includes(column.field)

        return accumulator
      }

      accumulator[column.field] = !PARTNER_INVOICE_DEFAULT_HIDDEN_FIELDS.includes(
        column.field as PartnerInvoicesTableFields,
      )

      return accumulator
    }, {})
  }, [partnerInvoiceColumns, includedColumns, hasStoredColumnPreference, excludedColumnsOverride])

  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      // The first change is what makes the stored list authoritative from here on.
      setHasStoredColumnPreference(true)

      const nextExcludedColumns = partnerInvoiceColumns.reduce<Array<string>>(
        (accumulator, column) => {
          if (UNHIDEABLE_COLUMNS.includes(column.field)) {
            return accumulator
          }

          if (model[column.field] === false) {
            accumulator.push(column.field)
          }

          return accumulator
        },
        [],
      )

      setExcludedColumnsOverride(nextExcludedColumns)

      // Read fresh rather than trusting a mount-time snapshot of `pinned`, so a concurrently
      // written pin preference cannot be clobbered by this write.
      const persistentStateRaw = localStorage.getItem(PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY)
      const persistentState: StoredColumnsState | null = persistentStateRaw
        ? JSON.parse(persistentStateRaw)
        : null

      localStorage.setItem(
        PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY,
        JSON.stringify({
          excluded: nextExcludedColumns,
          pinned: persistentState?.pinned || [],
        }),
      )
    },
    [partnerInvoiceColumns],
  )

  // No pagination: every nested row is already in memory. `sortingMode="client"` for the same
  // reason — sorting here reorders a complete set and cannot misrepresent anything.
  return (
    <div css={tw`box-border h-full`}>
      <GridTable<PartnerInvoice>
        rows={composedInvoice.partnerInvoices}
        columns={partnerInvoiceColumns}
        getRowId={getRowId}
        sortingMode="client"
        className={nestedTableStyles}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        tableDataTestId={`partner-invoices-table-${composedInvoice.id}`}
        rowDataTestIdPrefix="partner-invoices-list"
      />
    </div>
  )
}

export const PartnerInvoicesTable = memo(PartnerInvoicesTableComponent)
